import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axiosInstance from "../../../../axiosConfig";

const MerchantView = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const uuid = searchParams.get("uuid");

  const [loading, setLoading] = useState(true);
  const [bankLoading, setBankLoading] = useState(true);
  const [error, setError] = useState(null);

  const [viewInfo, setViewInfo] = useState(null); // full response.result
  const [bankAccounts, setBankAccounts] = useState([]);

  // -------- Helpers --------
  const safeJoin = (arr) => (Array.isArray(arr) && arr.length ? arr.join(", ") : "-");

  const formatDateTime = (s) => {
    if (!s) return "-";
    const d = new Date(s.replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Build image URL using localImageServer + fullPath
  const buildDocUrl = (localImageServer, fullPath) => {
    if (!fullPath) return "";
    const base = (localImageServer || "").replace(/\/+$/, "");
    const path = String(fullPath).replace(/^\/+/, "");
    return base ? `${base}/${path}` : path;
  };

  // -------- Fetch merchant view-info --------
  useEffect(() => {
    const fetchViewInfo = async () => {
      if (!uuid) {
        setError("Missing merchant uuid in URL. Example: ?uuid=EE_XXXX");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const res = await axiosInstance.get(
          `/internal-api/v1/client-web/entity-panel/config/${encodeURIComponent(uuid)}/view-info`
        );

        if (res.data?.status && res.data?.result) {
          setViewInfo(res.data.result);
        } else {
          setError("Failed to load merchant view info.");
        }
      } catch (e) {
        console.error("View info error:", e);
        setError("Failed to load merchant view info. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchViewInfo();
  }, [uuid]);

  // -------- Fetch bank accounts (filter by uuid client-side) --------
  useEffect(() => {
    const fetchBankAccounts = async () => {
      if (!uuid) return;

      setBankLoading(true);
      try {
        const res = await axiosInstance.get(
          `/internal-api/v1/client-web/entity-panel/registration/entity-bank-accounts`,
          { params: { start: 0, length: 100 } }
        );

        const rows = res.data?.data || [];
        const filtered = rows.filter((r) => r.entityUuid === uuid);
        setBankAccounts(filtered);
      } catch (e) {
        console.error("Bank accounts error:", e);
        setBankAccounts([]);
      } finally {
        setBankLoading(false);
      }
    };

    fetchBankAccounts();
  }, [uuid]);

  const entity = viewInfo?.entity;
  const branches = viewInfo?.entityBranches || [];
  const documents = viewInfo?.entityDocuments || [];
  const localImageServer = viewInfo?.localImageServer || "";

  const pageTitle = useMemo(() => {
    if (!entity?.name) return "Merchant View";
    return `Merchant View - ${entity.name}`;
  }, [entity?.name]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <div className="w-4 h-4 border-b-2 border-brand-blue rounded-full animate-spin" />
          Loading merchant...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="p-4 text-sm text-red-700 border rounded-lg bg-red-50 border-red-200">
          {error}
        </div>
        <button
          className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-brand-blue hover:bg-brand-blue-dark"
          onClick={() => navigate(-1)}
        >
          Back
        </button>
      </div>
    );
  }

  if (!entity) {
    return (
      <div className="space-y-4">
        <div className="p-4 text-sm bg-white border rounded-lg text-slate-700 border-slate-200">
          No merchant data found.
        </div>
        <button
          className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-brand-blue hover:bg-brand-blue-dark"
          onClick={() => navigate(-1)}
        >
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{pageTitle}</h1>
          <div className="mt-1 text-sm text-slate-500">
            Home <span className="mx-1.5 text-slate-400">/</span>
            Merchants <span className="mx-1.5 text-slate-400">/</span>
            <span className="font-semibold text-slate-700">{entity.name || "View"}</span>
          </div>
          <div className="mt-1 text-xs text-slate-400">
            UUID: <span className="font-mono">{entity.uuid}</span>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            className="px-4 py-2.5 text-sm font-semibold bg-white border rounded-lg border-slate-300 text-slate-600 hover:bg-slate-50"
            onClick={() => navigate(-1)}
          >
            Back
          </button>

          <button
            className="px-4 py-2.5 text-sm font-semibold text-white rounded-lg shadow-sm bg-brand-blue hover:bg-brand-blue-dark"
            onClick={() => navigate(`/EditNavigationTabs?uuid=${encodeURIComponent(entity.uuid)}`)}
          >
            Edit
          </button>
        </div>
      </div>

      {/* Entity Info */}
      <div className="p-6 bg-white border rounded-xl border-slate-200">
        <h2 className="mb-4 text-sm font-bold text-slate-900">Merchant Details</h2>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <InfoRow label="Name" value={entity.name || "-"} />
          <InfoRow label="Registration / Code" value={entity.registration || entity.entityCode || "-"} />
          <InfoRow label="Status" value={String(entity.status ?? "-")} />
          <InfoRow label="Is Active" value={entity.isActive ? "Yes" : "No"} />
          {/* <InfoRow label="Entity State" value={entity.entityState?.name || "-"} /> */}
          <InfoRow label="Website" value={entity.website ? decodeURIComponent(entity.website) : "-"} />
          <InfoRow label="City" value={entity.city?.name || "-"} />
          {/* <InfoRow label="District" value={entity.district?.name || "-"} /> */}
          {/* <InfoRow label="Province" value={entity.province?.name || "-"} /> */}
          {/* <InfoRow label="Created At" value={formatDateTime(entity.createdAt)} /> */}
          {/* <InfoRow label="Updated At" value={formatDateTime(entity.updatedAt)} /> */}
          <InfoRow label="Phones" value={safeJoin(entity.contactNos)} />
          <InfoRow label="Emails" value={safeJoin(entity.emails)} />
        </div>

        <div className="mt-3">
          <div className="mb-1 text-xs text-slate-500">Address</div>
          <div className="p-3 text-sm border rounded-lg bg-slate-50 border-slate-100 text-slate-700">
            {entity.fullAddress || "-"}
          </div>
        </div>

        <div className="mt-3">
          <div className="mb-1 text-xs text-slate-500">Description</div>
          <div className="p-3 text-sm border rounded-lg bg-slate-50 border-slate-100 text-slate-700">
            {entity.description || "-"}
          </div>
        </div>

        {entity.geoLocation && (
          <div className="mt-3">
            <div className="mb-1 text-xs text-slate-500">Geo Location</div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <InfoRow label="Latitude" value={String(entity.geoLocation.latitude ?? "-")} />
              <InfoRow label="Longitude" value={String(entity.geoLocation.longitude ?? "-")} />
              <InfoRow label="Altitude" value={String(entity.geoLocation.altitude ?? "-")} />
            </div>
          </div>
        )}
      </div>

      {/* Branches */}
      <div className="p-6 bg-white border rounded-xl border-slate-200">
        <h2 className="mb-4 text-sm font-bold text-slate-900">Branches</h2>

        {branches.length === 0 ? (
          <div className="text-sm text-slate-500">No branches found.</div>
        ) : (
          <div className="space-y-3">
            {branches.map((b) => (
              <div key={b.uuid} className="p-4 border rounded-lg bg-slate-50 border-slate-100">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-800">{b.name || "Branch"}</div>
                    <div className="text-xs text-slate-500">
                      UUID: <span className="font-mono">{b.uuid}</span>
                    </div>
                  </div>
                  <div className="text-xs">
                    <span className={`px-2 py-0.5 rounded-full ${b.isActive ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                      {b.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-2 mt-2 md:grid-cols-2">
                  <InfoRow label="Branch Code" value={b.branchCode || "-"} />
                  <InfoRow label="Website" value={b.website ? decodeURIComponent(b.website) : "-"} />
                  <InfoRow label="Phones" value={safeJoin(b.contactNos)} />
                  <InfoRow label="Emails" value={safeJoin(b.emails)} />
                </div>

                <div className="mt-2">
                  <div className="mb-1 text-xs text-slate-500">Address</div>
                  <div className="p-3 text-sm bg-white border rounded-lg border-slate-200 text-slate-700">
                    {b.fullAddress || "-"}
                  </div>
                </div>

                {b.geoLocation && (
                  <div className="grid grid-cols-1 gap-2 mt-2 md:grid-cols-3">
                    <InfoRow label="Latitude" value={String(b.geoLocation.latitude ?? "-")} />
                    <InfoRow label="Longitude" value={String(b.geoLocation.longitude ?? "-")} />
                    <InfoRow label="Location Name" value={b.geoLocation.name || "-"} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Documents */}
      <div className="p-6 bg-white border rounded-xl border-slate-200">
        <h2 className="mb-4 text-sm font-bold text-slate-900">Documents</h2>

        {documents.length === 0 ? (
          <div className="text-sm text-slate-500">No documents found.</div>
        ) : (
          <div className="space-y-3">
            {documents.map((doc) => (
              <div key={doc.uuid} className="p-4 border rounded-lg bg-slate-50 border-slate-100">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-800">
                      {doc.guideline?.name || "Document"}
                    </div>
                    <div className="text-xs text-slate-500">
                      Code: {doc.guideline?.code || "-"} • UUID:{" "}
                      <span className="font-mono">{doc.uuid}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-2 mt-2 md:grid-cols-2">
                  <InfoRow label="Issuer" value={doc.issuer || "-"} />
                  <InfoRow label="Note" value={doc.note || "-"} />
                  <InfoRow label="Issued At" value={formatDateTime(doc.issuedAt)} />
                  <InfoRow label="Expired At" value={formatDateTime(doc.expiredAt)} />
                </div>

                {/* Files */}
                <div className="mt-3">
                  <div className="mb-2 text-xs text-slate-500">Files</div>
                  {Array.isArray(doc.documentFiles) && doc.documentFiles.length ? (
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      {doc.documentFiles.map((f) => {
                        const url = buildDocUrl(localImageServer, f.fullPath);
                        return (
                          <div key={f.uuid} className="p-3 bg-white border rounded-lg border-slate-200">
                            <div className="text-xs font-medium text-slate-700">
                              {f.documentName}
                            </div>
                            <div className="mt-1 font-mono text-xs text-slate-500">
                              {f.fullPath}
                            </div>

                            {url ? (
                              <div className="mt-2">
                                <img
                                  src={url}
                                  alt={f.documentName || "document"}
                                  className="object-contain w-full border rounded max-h-60"
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                                <div className="mt-2">
                                  <a
                                    href={url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-xs text-brand-blue hover:underline"
                                  >
                                    Open file
                                  </a>
                                </div>
                              </div>
                            ) : (
                              <div className="mt-2 text-xs text-slate-500">
                                File URL not available.
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500">No document files found.</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bank Accounts */}
      <div className="p-6 bg-white border rounded-xl border-slate-200">
        <h2 className="mb-4 text-sm font-bold text-slate-900">Bank Accounts</h2>

        {bankLoading ? (
          <div className="text-sm text-slate-500">Loading bank accounts...</div>
        ) : bankAccounts.length === 0 ? (
          <div className="text-sm text-slate-500">No bank accounts found for this merchant.</div>
        ) : (
          <div className="overflow-x-auto border rounded-lg border-slate-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-slate-50 border-slate-200">
                  <th className="px-4 py-3 text-xs font-semibold text-left text-slate-500">Bank</th>
                  <th className="px-4 py-3 text-xs font-semibold text-left text-slate-500">Branch</th>
                  <th className="px-4 py-3 text-xs font-semibold text-left text-slate-500">Account Type</th>
                  <th className="px-4 py-3 text-xs font-semibold text-left text-slate-500">Account No</th>
                  <th className="px-4 py-3 text-xs font-semibold text-left text-slate-500">Holder</th>
                  <th className="px-4 py-3 text-xs font-semibold text-left text-slate-500">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {bankAccounts.map((a) => (
                  <tr key={a.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-700">{a.bankName || "-"}</td>
                    <td className="px-4 py-3 text-slate-700">{a.branchName || "-"}</td>
                    <td className="px-4 py-3 text-slate-700">{a.accountType || "-"}</td>
                    <td className="px-4 py-3 font-mono text-slate-700">{a.accountNumber || "-"}</td>
                    <td className="px-4 py-3 text-slate-700">{a.holderName || "-"}</td>
                    <td className="px-4 py-3 text-slate-500">{formatDateTime(a.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const InfoRow = ({ label, value }) => {
  return (
    <div className="p-4 border rounded-lg bg-slate-50 border-slate-100">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-sm break-words text-slate-800">{value}</div>
    </div>
  );
};

export default MerchantView;
